import HomeIcon from "@mui/icons-material/Home";
import { Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useSsoMutation } from "Auth/Api/Mutations/useSsoMutation";
import { OAUTH_STATE_KEY } from "Auth/Utils/oauth";
import { IdentityProvider } from "Infrastructure/Api/Api";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { BlButton } from "Shared/Buttons/BlButton";
import { UnauthenticatedLayout } from "Shared/Layouts/UnauthenticatedLayout";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";

export const AuthCallbackPage: React.FunctionComponent = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const ssoMutation = useSsoMutation();
	const [error, setError] = useState<string | null>(null);
	const hasProcessed = useRef(false);

	// Watch for mutation ERROR state - show error message
	useEffect(() => {
		if (ssoMutation.isError) {
			cleanupSessionStorage();
			const errorMessage =
				ssoMutation.error instanceof Error
					? ssoMutation.error.message
					: "Přihlášení selhalo";
			setError(errorMessage);
		}
	}, [ssoMutation.isError, ssoMutation.error]);

	// Process OAuth callback
	useEffect(() => {
		if (hasProcessed.current) {
			return;
		}

		const code = searchParams.get("code");
		const stateFromUrl = searchParams.get("state");
		const errorParam = searchParams.get("error");
		const errorDescription = searchParams.get("error_description");

		// Handle OAuth errors from Microsoft
		if (errorParam) {
			setError(errorDescription || errorParam);
			cleanupSessionStorage();
			return;
		}

		// Validate authorization code exists
		if (!code) {
			setError("Authorization code not found in callback URL");
			cleanupSessionStorage();
			return;
		}

		// Validate state parameter (CSRF protection)
		const storedState = sessionStorage.getItem(OAUTH_STATE_KEY);
		if (!stateFromUrl || !storedState || stateFromUrl !== storedState) {
			setError("Invalid state parameter - possible CSRF attack");
			cleanupSessionStorage();
			return;
		}

		hasProcessed.current = true;

		// Trigger mutation with success callback for navigation
		ssoMutation.mutate({
			token: code,
			identityProvider: IdentityProvider.EntraId,
			onSuccessCallback: () => {
				cleanupSessionStorage();
				navigate(AppRouting.Home.path());
			},
		});
	}, [searchParams, ssoMutation.mutate, navigate]);

	if (error) {
		return (
			<UnauthenticatedLayout title="Chyba přihlášení">
				<Typography variant="h4" gutterBottom>
					Chyba přihlášení
				</Typography>
				<Typography color="error" sx={{ mb: 3 }}>
					{error}
				</Typography>
				<UnstyledLink to={AppRouting.SignIn.path()}>
					<BlButton
						startIcon={<HomeIcon />}
						variant="contained"
						color="primary"
					>
						Zpět na přihlášení
					</BlButton>
				</UnstyledLink>
			</UnauthenticatedLayout>
		);
	}

	return (
		<UnauthenticatedLayout title="Přihlašování">
			<Typography>Probíhá ověření...</Typography>
		</UnauthenticatedLayout>
	);
};

function cleanupSessionStorage(): void {
	sessionStorage.removeItem(OAUTH_STATE_KEY);
}
